import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiShoppingBag } from "react-icons/fi";
import { GiPlantRoots } from "react-icons/gi";
import { MdOutlineFactory, MdOutlineRecycling } from "react-icons/md";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { getProductColor } from "../config/productColors";
import { CartContext } from "./CartContext";
import { ProductCard } from "./Shop";
import "../css/productdetails.scss";

const attributeLabels = {
  material: "Material",
  origin: "Origin",
  sole: "Sole",
  fit: "Fit guidance",
  lining: "Lining",
  dimensions: "Dimensions",
  hardware: "Hardware",
  width: "Width",
  weight: "Weight",
  care: "Care",
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, wishlist, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size[0] || "");
    setSelectedColor(product.colors[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    document.title = `${product.name} — ${siteConfig.brandName}`;
  }, [product]);

  const related = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.category === product.category && item.id !== product.id
    );
    return [
      ...sameCategory,
      ...productData.filter(
        (item) => item.id !== product.id && item.category !== product.category
      ),
    ].slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-not-found page-shell shell">
        <p className="eyebrow">Error 404</p>
        <h1>This style could not be found.</h1>
        <Link className="btn btn--outline" to="/shop">
          Return to shop
        </Link>
      </main>
    );
  }

  const isFavorite = wishlist.includes(product.id);
  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/cartpage");
  };

  return (
    <main className="product-detail">
      <nav className="product-breadcrumb shell" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/shop">Shop</Link>
        <span>/</span>
        <span>{product.name}</span>
      </nav>

      <div className="product-layout shell">
        <section
          className="product-gallery"
          aria-label={`${product.name} photos`}
        >
          {product.images.map((image, index) => (
            <button
              type="button"
              key={image}
              className={index === selectedImage ? "is-selected" : ""}
              onClick={() => setSelectedImage(index)}
              aria-label={`Show photo ${index + 1}`}
            >
              <img
                src={image}
                alt={`${product.name} — view ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
        </section>

        <aside className="product-purchase">
          {product.badge && (
            <p className="product-purchase__badge">{product.badge}</p>
          )}
          <p className="product-purchase__category">{product.category}</p>
          <h1>{product.name}</h1>
          <div className="product-purchase__price">
            <span>{formatPrice(product.price)}</span>
            {product.compareAtPrice && (
              <del>{formatPrice(product.compareAtPrice)}</del>
            )}
          </div>
          <p className="product-purchase__tax">
            Taxes included. Delivery calculated at checkout.
          </p>

          <div
            className="product-rating"
            aria-label={`Rated ${product.rating} out of 5`}
          >
            <span>★★★★★</span> {product.rating} · {product.reviews} reviews
          </div>

          <div className="product-option">
            <div className="product-option__heading">
              <span>Colour</span>
              <b>{selectedColor}</b>
            </div>
            <div className="color-options">
              {product.colors.map((color) => (
                <button
                  key={color}
                  className={selectedColor === color ? "is-active" : ""}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  aria-label={color}
                  title={color}
                  style={{ backgroundColor: getProductColor(color) }}
                />
              ))}
            </div>
          </div>

          <div className="product-option">
            <div className="product-option__heading">
              <span>Size</span>
            </div>
            <div className="size-options">
              {product.size.map((size) => (
                <button
                  key={size}
                  className={selectedSize === size ? "is-active" : ""}
                  type="button"
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div className="product-actions">
            <div className="quantity-picker" aria-label="Quantity">
              <button
                type="button"
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
              >
                −
              </button>
              <span>{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((value) => value + 1)}
              >
                +
              </button>
            </div>
            <button
              className="btn product-add"
              type="button"
              onClick={handleAddToCart}
            >
              <FiShoppingBag /> Add to cart
            </button>
            <button
              className={`product-wishlist ${isFavorite ? "is-active" : ""}`}
              type="button"
              onClick={() => toggleWishlist(product.id)}
              aria-pressed={isFavorite}
              aria-label="Add to wishlist"
            >
              <FiHeart />
            </button>
          </div>

          <div className="product-values">
            <div>
              <MdOutlineRecycling />
              <span>
                Protective
                <br />
                packaging
              </span>
            </div>
            <div>
              <GiPlantRoots />
              <span>
                Care
                <br />
                guidance
              </span>
            </div>
            <div>
              <MdOutlineFactory />
              <span>
                Origin on
                <br />
                the label
              </span>
            </div>
          </div>

          <div className="product-accordions">
            <details open>
              <summary>
                Description <FiChevronDown />
              </summary>
              <p>{product.description}</p>
            </details>
            <details>
              <summary>
                Composition & Details <FiChevronDown />
              </summary>
              <dl>
                {Object.entries(product.attributes).map(([key, value]) => (
                  <React.Fragment key={key}>
                    <dt>{attributeLabels[key] || key}</dt>
                    <dd>{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </details>
            <details>
              <summary>
                Care Instructions <FiChevronDown />
              </summary>
              <p>
                Clean gently with a soft, slightly damp sponge and mild soap.
                Air dry away from direct heat. No leather-care products are
                required.
              </p>
            </details>
            <details>
              <summary>
                Delivery & Returns <FiChevronDown />
              </summary>
              <p>
                Dispatched within 1 to 3 business days. You may request a return
                within 14 days of receipt, subject to our conditions.
              </p>
            </details>
          </div>
        </aside>
      </div>

      <section className="product-related">
        <header className="section-heading shell">
          <div>
            <p className="eyebrow">You may also like</p>
            <h2>More to Explore</h2>
          </div>
          <Link className="link-arrow" to="/shop">
            View all
          </Link>
        </header>
        <div className="product-grid shell">
          {related.map((item) => (
            <ProductCard key={item.id} product={item} />
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
